import { DragEvent, useId, useRef, useState } from "react";

import {
  formatBytes,
  IFileValidationRules,
  toAcceptAttribute,
  validateFile,
} from "../../../../utils/fileUpload";
import { FilterButton } from "../AdminPage.styles";
import {
  HiddenFileInput,
  UploadActions,
  UploadDropArea,
  UploadErrorText,
  UploadFieldContainer,
  UploadFileName,
  UploadHelperText,
  UploadLabel,
  UploadProgressBar,
  UploadProgressTrack,
} from "./FileUploadField.styles";

export interface IFileUploadFieldProps extends IFileValidationRules {
  label?: string;
  /** The picked file, owned by the parent so it can submit it. */
  value: File | null;
  onChange: (file: File | null) => void;
  /** Extra guidance under the field; the accepted types and size cap are appended. */
  helperText?: string;
  /** Server-side error to show alongside the client-side validation message. */
  error?: string;
  disabled?: boolean;
  /** 0–100 while uploading; hidden when undefined. */
  progress?: number;
}

/**
 * File picker for the migrated /admin pages (EN-5148) — click or drop, with
 * type + size validation before anything is sent. Retool's dropzone and
 * file-input widgets accepted whatever the user picked and only found out on
 * the server; this rejects the obvious cases up front and leaves the rest to
 * the API (surface those with `useFileUpload`'s returned `errors`).
 *
 *   <FileUploadField
 *     label="Patient CSV"
 *     accept={[".csv", "text/csv"]}
 *     maxBytes={1024 * 1024}
 *     value={file}
 *     onChange={setFile}
 *   />
 */
export function FileUploadField({
  label,
  value,
  onChange,
  helperText,
  error,
  disabled = false,
  progress,
  accept,
  maxBytes,
}: IFileUploadFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const acceptFile = (file: File | undefined) => {
    if (!file) return;

    const problem = validateFile(file, { accept, maxBytes });
    setValidationError(problem);
    // A rejected file also clears any previous selection, so the parent can
    // never submit a file the user was just told is invalid.
    onChange(problem ? null : file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    acceptFile(event.dataTransfer.files?.[0]);
  };

  const handleClear = () => {
    setValidationError(null);
    onChange(null);
    if (inputRef.current) {
      // Without this the same file can't be re-picked (no change event).
      inputRef.current.value = "";
    }
  };

  // `accept` carries both extensions and MIME types, because the input's
  // accept attribute and the validation want both. A person only wants the
  // extensions: the logo field was printing ".svg, .png, image/svg+xml,
  // image/png", which says one thing twice. MIME types are shown only where
  // a caller gave nothing else.
  const shownTypes = (accept ?? []).filter((type) => type.startsWith("."));
  const typeList = (shownTypes.length > 0 ? shownTypes : accept ?? []).join(
    ", "
  );

  const constraints = [
    typeList || undefined,
    maxBytes !== undefined ? `up to ${formatBytes(maxBytes)}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  const shownError = validationError ?? error;
  const isUploading = progress !== undefined && progress > 0 && progress < 100;

  function resolveState() {
    if (disabled) return "disabled" as const;
    if (shownError) return "invalid" as const;
    if (isDragging) return "dragging" as const;
    return "idle" as const;
  }

  return (
    <UploadFieldContainer>
      {label && <UploadLabel htmlFor={inputId}>{label}</UploadLabel>}

      <UploadDropArea
        state={resolveState()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <UploadFileName empty={!value}>
          {value
            ? `${value.name} (${formatBytes(value.size)})`
            : "No file selected"}
        </UploadFileName>

        <UploadActions>
          {value && (
            <FilterButton
              type="button"
              variant="secondary"
              onClick={handleClear}
              disabled={disabled}
            >
              Remove
            </FilterButton>
          )}
          <FilterButton
            type="button"
            variant="primary"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            {value ? "Change file" : "Choose file"}
          </FilterButton>
        </UploadActions>

        <HiddenFileInput
          id={inputId}
          ref={inputRef}
          type="file"
          accept={toAcceptAttribute(accept)}
          disabled={disabled}
          onChange={(event) => acceptFile(event.target.files?.[0])}
        />
      </UploadDropArea>

      {isUploading && (
        <UploadProgressTrack
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <UploadProgressBar css={{ width: `${progress}%` }} />
        </UploadProgressTrack>
      )}

      {shownError ? (
        <UploadErrorText role="alert">{shownError}</UploadErrorText>
      ) : (
        (helperText || constraints) && (
          <UploadHelperText>
            {[helperText, constraints].filter(Boolean).join(" · ")}
          </UploadHelperText>
        )
      )}
    </UploadFieldContainer>
  );
}
